import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { LenderRiskProfilingService } from '../tokens/lender-risk-profiling.token';
import {
  Stage7ActiveModel,
  LenderScoringModel,
  LenderModelTranslation,
} from '../models/lender-scoring-model.model';
import { RatingGrade } from '../models/risk-rating.model';
import { API_PATHS } from '../config/api-paths';

/**
 * REST-only implementation of `LenderRiskProfilingService` (EM-158 / ST-12).
 *
 * Every method calls the real `/services/embedded/api/...` endpoint via
 * `HttpClient`. Rule 07 + Rule 10 — no mock fallback, no internal in-memory
 * state, no `Mock*` sibling class.
 * Playwright tests intercept the HTTP layer via `page.route()`.
 *
 * Authority: ROLE_MASTER_ADMIN (enforced by the portal roleGuard and the
 * backend `@PreAuthorize`). The auth interceptor attaches the signed-in user's
 * bearer token automatically.
 */
@Injectable()
export class LenderRiskProfilingRestService implements LenderRiskProfilingService {
  private readonly http = inject(HttpClient);

  private readonly activeModelsUrl =
    `${API_PATHS.EMBEDDED_API_BASE}/risk-rating/lender-scoring-models/active`;

  private readonly scoringModelsUrl =
    `${API_PATHS.EMBEDDED_API_BASE}/lender-scoring-models`;

  private readonly translationsUrl =
    `${API_PATHS.EMBEDDED_API_BASE}/lender-model-translations`;

  // ── Active model list ──────────────────────────────────────────────────────

  /**
   * GET /api/risk-rating/lender-scoring-models/active
   */
  getActiveModels(): Observable<Stage7ActiveModel[]> {
    return this.http.get<Stage7ActiveModel[]>(this.activeModelsUrl);
  }

  // ── Model detail ───────────────────────────────────────────────────────────

  /**
   * GET /api/lender-scoring-models/{id}
   */
  getModelById(id: number): Observable<LenderScoringModel> {
    return this.http.get<LenderScoringModel>(`${this.scoringModelsUrl}/${id}`);
  }

  /**
   * PUT /api/lender-scoring-models/{id}
   */
  updateModel(id: number, dto: LenderScoringModel): Observable<LenderScoringModel> {
    return this.http.put<LenderScoringModel>(`${this.scoringModelsUrl}/${id}`, dto);
  }

  // ── Translations ───────────────────────────────────────────────────────────

  /**
   * GET /api/lender-model-translations?lenderScoringModelId.equals={modelId}&size=200
   *
   * The generated `LenderModelTranslationResource` uses JHipster criteria filtering.
   * The `lenderScoringModelId.equals` filter is the standard JHipster criteria param.
   * We request up to 200 rows (far more than any model will ever have).
   * The backend returns `LenderModelTranslationDTO` with a nested `lenderScoringModel`
   * object; we project to the flat `LenderModelTranslation` shape for the UI.
   */
  getTranslations(modelId: number): Observable<LenderModelTranslation[]> {
    const params = new HttpParams()
      .set('lenderScoringModelId.equals', modelId.toString())
      .set('size', '200');
    return this.http
      .get<BackendTranslationDTO[]>(this.translationsUrl, { params })
      .pipe(map(rows => rows.map(r => this.mapTranslation(r, modelId))));
  }

  /**
   * POST /api/lender-model-translations
   */
  createTranslation(
    modelId: number,
    nativeValue: string,
    platformGrade: RatingGrade,
  ): Observable<LenderModelTranslation> {
    const body: BackendTranslationCreateDTO = {
      nativeValue,
      platformGrade,
      lenderScoringModel: { id: modelId },
    };
    return this.http
      .post<BackendTranslationDTO>(this.translationsUrl, body)
      .pipe(map(r => this.mapTranslation(r, modelId)));
  }

  /**
   * PUT /api/lender-model-translations/{id}
   */
  updateTranslation(
    id: number,
    modelId: number,
    nativeValue: string,
    platformGrade: RatingGrade,
  ): Observable<LenderModelTranslation> {
    const body: BackendTranslationDTO = {
      id,
      nativeValue,
      platformGrade,
      lenderScoringModel: { id: modelId } as BackendTranslationDTO['lenderScoringModel'],
    };
    return this.http
      .put<BackendTranslationDTO>(`${this.translationsUrl}/${id}`, body)
      .pipe(map(r => this.mapTranslation(r, modelId)));
  }

  /**
   * DELETE /api/lender-model-translations/{id}
   */
  deleteTranslation(id: number): Observable<void> {
    return this.http
      .delete<void>(`${this.translationsUrl}/${id}`);
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private mapTranslation(raw: BackendTranslationDTO, modelId: number): LenderModelTranslation {
    return {
      id: raw.id ?? null,
      nativeValue: raw.nativeValue,
      platformGrade: raw.platformGrade,
      lenderScoringModelId: raw.lenderScoringModel?.id ?? modelId,
    };
  }
}

/**
 * Raw shape returned by `GET/POST/PUT /api/lender-model-translations`.
 * Private to this file — the UI sees the flat `LenderModelTranslation` shape.
 */
interface BackendTranslationDTO {
  id: number | null;
  nativeValue: string;
  platformGrade: RatingGrade;
  lenderScoringModel: { id: number; name?: string; version?: string };
}

/** Minimal body shape required by `POST /api/lender-model-translations`. */
interface BackendTranslationCreateDTO {
  nativeValue: string;
  platformGrade: RatingGrade;
  lenderScoringModel: { id: number };
}
